import type {
  NavigatorProvider,
  NavigatorResource,
  NavigatorResultsViewModel,
} from './navigator-results.types';
import type { MatchedSpecialtyApi } from './navigator-matching.types';

/**
 * Map one live matched provider (from GET /find-providers/specialties) into the
 * shared NavigatorProvider shape used by NavigatorResults.
 */
export const mapLiveProvider = (
  provider: MatchedSpecialtyApi['providers'][number],
  specialtyName: string,
): NavigatorProvider => ({
  id: provider.provider_id,
  name: provider.provider_name,
  specialtyName,
  categoryLabel: provider.provider_category_name,
  bio: provider.overview,
  rating: provider.rating,
  location: provider.location,
  phone: provider.contact_information,
  email: provider.email,
  experience: provider.experience,
  credentials: provider.credentials ?? undefined,
  areasOfFocus: provider.specialties ?? undefined,
  aiReason: provider.ai_reason?.trim() ? provider.ai_reason : null,
  aiBadges: provider.ai_badges ?? undefined,
  rankOrder: provider.rank_order ?? null,
  isSelected: provider.user_selected ?? false,
  score: provider.score ?? null,
  relevanceScore: provider.relevance_score ?? null,
  qualityScore: provider.quality_score ?? null,
  isBestMatch: provider.is_best_match ?? false,
  specialtyDeliveryModes: provider.specialtyDeliveryModes ?? [],
});

export type BuildNavigatorViewModelInput = {
  planTitle: string;
  careStageLabel: string;
  summaryBullets: string[];
  categories: MatchedSpecialtyApi[];
  /** Care-stage-level resources (optional; FP supplies these via a renderKeyResources override instead). */
  careStageResources?: NavigatorResource[];
};

/**
 * Normalize the live find-providers matches into the shared NavigatorResults
 * view-model. Shared by the care-seeker results page and the FP Q&A results
 * page so both render identical categories/providers in the same order
 * (first category = primary).
 */
export const buildNavigatorViewModelFromLive = ({
  planTitle,
  careStageLabel,
  summaryBullets,
  categories,
  careStageResources = [],
}: BuildNavigatorViewModelInput): NavigatorResultsViewModel => ({
  planTitle,
  careStageLabel,
  summaryBullets,
  careStageResources,
  categories: categories.map((specialty, index) => ({
    id: specialty.specialty_id,
    name: specialty.name,
    isPrimary: index === 0,
    resources: [],
    whyYouNeedThem: specialty.why_you_need_them?.trim() ? specialty.why_you_need_them : null,
    whatTheyDo: specialty.what_they_do?.trim() ? specialty.what_they_do : null,
    providers: specialty.providers.map((provider) => mapLiveProvider(provider, specialty.name)),
  })),
});
